// src/api/v3/admin_accounts-api-v3.js
import { authHeaders } from './_auth';

const API_BASE = process.env.REACT_APP_API_BASE;

function buildErrorMessage(json, fallback) {
  if (!json) return fallback;

  if (Array.isArray(json.errors)) {
    return json.errors
      .map((item) => {
        if (typeof item === 'string') return item;
        if (item?.message) return item.message;
        return JSON.stringify(item);
      })
      .join(', ');
  }

  if (json.errors && typeof json.errors === 'object') {
    return Object.entries(json.errors)
      .map(([field, messages]) => {
        if (Array.isArray(messages)) return `${field} ${messages.join(', ')}`;
        return `${field} ${messages}`;
      })
      .join(', ');
  }

  return json?.error || json?.message || fallback;
}

async function handleRes(res) {
  if (res.status === 204) return null;

  let json = null;

  try {
    json = await res.json();
  } catch (_) {
    json = null;
  }

  if (!res.ok) {
    const err = new Error(buildErrorMessage(json, `HTTP ${res.status}`));

    // Keep status accessible for page-level handling, especially 401/403.
    err.status = res.status;
    err.payload = json;

    // Compatibility if some page still checks axios-style error.
    err.response = {
      status: res.status,
      data: json,
    };

    throw err;
  }

  return json;
}

const AdminAccountsAPI = {
  async list(params = {}) {
    const url = new URL(`${API_BASE}/api/v3/admin_accounts`);

    Object.entries(params).forEach(([key, value]) => {
      if (value == null || value === '') return;
      url.searchParams.set(key, String(value));
    });

    const res = await fetch(url.toString(), {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    });

    return handleRes(res);
  },

  async roles() {
    const res = await fetch(`${API_BASE}/api/v3/admin_accounts/roles`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    });

    return handleRes(res);
  },

  async show(id) {
    const res = await fetch(`${API_BASE}/api/v3/admin_accounts/${id}`, {
      method: 'GET',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    });

    return handleRes(res);
  },

  async create(payload) {
    const res = await fetch(`${API_BASE}/api/v3/admin_accounts`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({
        admin: payload,
      }),
    });

    return handleRes(res);
  },

  async update(id, payload) {
    const res = await fetch(`${API_BASE}/api/v3/admin_accounts/${id}`, {
      method: 'PUT',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...authHeaders(),
      },
      body: JSON.stringify({
        admin: payload,
      }),
    });

    return handleRes(res);
  },

  async destroy(id) {
    const res = await fetch(`${API_BASE}/api/v3/admin_accounts/${id}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        ...authHeaders(),
      },
    });

    return handleRes(res);
  },
};

export default AdminAccountsAPI;
